import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { employeeService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus, Search, Filter, Pencil, Trash2, X, ExternalLink, UserCheck } from 'lucide-react';

const STATUS_MAP = {
  attivo: { label: 'Attivo', cls: 'bg-green-100 text-green-700' },
  non_attivo: { label: 'Non attivo', cls: 'bg-red-100 text-red-700' },
};

const EMPTY_FORM = { employee_code: '', first_name: '', last_name: '', department: '', role: '', hire_date: '', status: 'attivo', notes: '' };

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ department: '', role: '', status: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const canEdit = ['admin', 'production', 'management'].includes(user?.role);

  const load = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      if (search) params.search = search;
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await employeeService.getAll(params);
      setEmployees(res.data);
    } catch { toast.error('Errore caricamento dipendenti'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, filters]);
  useEffect(() => {
    Promise.all([employeeService.getDepartments(), employeeService.getRoles()])
      .then(([d, r]) => { setDepartments(d.data); setRoles(r.data); }).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      employee_code: e.employee_code, first_name: e.first_name, last_name: e.last_name,
      department: e.department || '', role: e.role || '', hire_date: e.hire_date || '',
      status: e.status, notes: e.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.employee_code || !form.first_name || !form.last_name) { toast.error('Codice, Nome e Cognome obbligatori'); return; }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.employee_code = form.employee_code;
      payload.first_name = form.first_name;
      payload.last_name = form.last_name;
      payload.status = form.status || 'attivo';
      if (editing) { await employeeService.update(editing.id, payload); toast.success('Dipendente aggiornato'); }
      else { await employeeService.create(payload); toast.success('Dipendente creato'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo dipendente?')) return;
    try { await employeeService.delete(id); toast.success('Dipendente eliminato'); load(); } catch { toast.error('Errore eliminazione'); }
  };

  const hasFilters = Object.values(filters).some(v => v);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="employees-title">Dipendenti</h1>
          <p className="text-sm text-slate-500 mt-0.5">{employees.length} dipendenti</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input type="text" placeholder="Cerca dipendente..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-64 focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="employee-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="employee-filters-btn"
            className={hasFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}>
            <Filter className="w-4 h-4 mr-1" />Filtri
          </Button>
          {canEdit && <Button size="sm" onClick={openCreate} data-testid="new-employee-btn"><Plus className="w-4 h-4 mr-1" />Nuovo dipendente</Button>}
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="employee-filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Reparto</label>
              <select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-department">
                <option value="">Tutti</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Ruolo</label>
              <select value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-role">
                <option value="">Tutti</option>
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Stato</label>
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-status">
                <option value="">Tutti</option>
                <option value="attivo">Attivo</option>
                <option value="non_attivo">Non attivo</option>
              </select>
            </div>
          </div>
          {hasFilters && <div className="mt-3 flex justify-end"><button onClick={() => setFilters({ department: '', role: '', status: '' })} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Resetta filtri</button></div>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="employees-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : employees.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium text-slate-500">Nessun dipendente trovato</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Codice dipendente</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Cognome</th>
                <th className="px-4 py-3">Reparto</th><th className="px-4 py-3">Ruolo</th><th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3">Data assunzione</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map(emp => (
                  <tr key={emp.id} className="hover:bg-slate-50 transition-colors" data-testid={`employee-row-${emp.id}`}>
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{emp.employee_code}</td>
                    <td className="px-4 py-3 text-slate-700">{emp.first_name}</td>
                    <td className="px-4 py-3 text-slate-700">{emp.last_name}</td>
                    <td className="px-4 py-3 text-slate-600">{emp.department || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{emp.role || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_MAP[emp.status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_MAP[emp.status]?.label || emp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(emp.hire_date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Link to={`/employees/${emp.id}`} className="p-1 text-slate-400 hover:text-blue-600 rounded" data-testid={`open-employee-${emp.id}`} title="Apri scheda"><ExternalLink className="w-4 h-4" /></Link>
                        {canEdit && <button onClick={() => openEdit(emp)} className="p-1 text-slate-400 hover:text-blue-600 rounded" data-testid={`edit-employee-${emp.id}`} title="Modifica"><Pencil className="w-4 h-4" /></button>}
                        {user?.role === 'admin' && <button onClick={() => handleDelete(emp.id)} className="p-1 text-slate-400 hover:text-red-600 rounded" data-testid={`delete-employee-${emp.id}`} title="Elimina"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="employee-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Modifica Dipendente' : 'Nuovo Dipendente'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Codice dipendente *</label>
                  <input type="text" value={form.employee_code} onChange={e => setForm(f => ({ ...f, employee_code: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-employee-code" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Nome *</label>
                  <input type="text" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-first-name" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Cognome *</label>
                  <input type="text" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-last-name" /></div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Reparto</label>
                  <input type="text" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} list="dept-list-emp" className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-department" />
                  <datalist id="dept-list-emp">{departments.map(d => <option key={d} value={d} />)}</datalist></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Ruolo</label>
                  <input type="text" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} list="role-list-emp" className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-role" />
                  <datalist id="role-list-emp">{roles.map(r => <option key={r} value={r} />)}</datalist></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Data assunzione</label>
                  <input type="date" value={form.hire_date} onChange={e => setForm(f => ({ ...f, hire_date: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-hire-date" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Stato</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-status">
                    <option value="attivo">Attivo</option><option value="non_attivo">Non attivo</option>
                  </select></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Note</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={1} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-notes" /></div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Annulla</Button>
                <Button type="submit" size="sm" data-testid="employee-save-btn">{editing ? 'Salva' : 'Crea Dipendente'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
